import { useInfiniteQuery } from '@tanstack/react-query'
import useAxios from 'api/useAxios'
import {
  flattenPaginatedData,
  queryBuilder,
  recursiveCamelCase,
} from 'api/helper'
import { ICleanResponse } from 'api/types'
import { getNextPageParam } from 'api/useCollectionData'
import { type AlertsFiltersT } from 'utils/useAlertsFilters'
import useTracking, { TrackingTypes } from 'tracking/useTracking'

function trackFilters(filters: AlertsFiltersT, tracking: TrackingTypes) {
  const filterLabels = Object.fromEntries(
    Object.entries({
      diseases: filters?.diseases.map((item: any) => item.label),
      locations: filters?.locations.map((item: any) => item.label),
      pathogenFamilies: filters?.pathogenFamilies.map(
        (item: any) => item.label
      ),
      pathogenTypes: filters?.pathogenTypes.map((item: any) => item.label),
    }).filter(([, value]) => value.length > 0)
  )
  if (Object.keys(filterLabels).length > 0) {
    tracking.alertsFilters({ filter: filterLabels })
  }
}

export function useFilteredAlerts({
  filters,
  maxItems = 10,
  disableTracking,
}: {
  filters: AlertsFiltersT
  maxItems?: number
  disableTracking?: boolean
}) {
  const axios = useAxios()
  const [tracking] = useTracking()
  const flattenedFilters = getFlattenFilters(filters)

  const queryStringObj = {
    limit: maxItems,
    sort: '-date_published',
  }

  const {
    isLoading,
    isFetchingNextPage,
    data,
    isError,
    hasNextPage,
    isFetched,
    fetchNextPage,
  } = useInfiniteQuery(
    ['alerts', flattenedFilters],
    async ({ pageParam: offset }) => {
      const query = queryBuilder({ ...queryStringObj, offset })
      !disableTracking && trackFilters(filters, tracking)
      const response = await axios.post(
        `/alerts/results/?${query}`,
        flattenedFilters
      )

      const data = recursiveCamelCase(response.data.results)
      const res = {
        count: response.data.count,
        results: data.map((row: any) =>
          Object.fromEntries(
            Object.entries(row).map(([key, val]) => [key, val])
          )
        ) as ICleanResponse[],
      }
      return res
    },
    {
      enabled: !!filters, // Only run the query if filters are provided
      getNextPageParam: getNextPageParam,
    }
  )
  const flattenData = flattenPaginatedData(data?.pages)?.results

  return {
    isLoading,
    data: flattenData,
    maxItems,
    canFetchMore: hasNextPage,
    fetchMore: fetchNextPage,
    isFetchingNextPage,
    isError,
    isFetched,
    total: data?.pages[0]?.count,
  }
}

function getFlattenFilters(filters: AlertsFiltersT) {
  const flattenedFilters = {
    selectedDiseases: filters?.diseases.map((item: any) => item.value),
    selectedLocations: filters?.locations.map((item: any) => item.value),
    selectedPathogenFamilies: filters?.pathogenFamilies.map(
      (item: any) => item.value
    ),
    selectedPathogenTypes: filters?.pathogenTypes.map(
      (item: any) => item.value
    ),
  }

  return flattenedFilters as Record<string, string[]>
}
